import { useMemo } from "react";

import { usePrefetchQuery } from "@tanstack/react-query";

import { ContactUsThanksPortableModal } from "app/pages/sales-intelligence/common-components/ContactUsModal/ContactUsThanksPortableModal";
import { useContactUsThanksModal } from "app/pages/sales-intelligence/common-components/ContactUsModal/useContactUsThanksModal";
import { accountManagersQueryOptions } from "app/pages/sales-intelligence/sub-modules/account-manager/queries/useAccountManagersQuery";

import { Button } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { CrmManagementDisabledTooltip } from "app/pages/sales-intelligence/common-components/CrmManagementDisabledTooltip";
import { CrmIndustryTransitionAlertBanner } from "app/pages/sales-intelligence/modules/industry/components/CrmIndustryTransitionAlertBanner/CrmIndustryTransitionAlertBanner";
import { isCrmJobsTransitionProvider } from "app/pages/sales-intelligence/modules/industry/types/crmJobsTransition";
import InfoIconTooltip from "app/pages/sales-intelligence/common-components/tooltip/InfoIconTooltip/InfoIconTooltip";
import {
    StyledEnrichmentHeaderWrapper,
    StyledSectionSubtitle,
    StyledSectionTitle,
    StyledIntegrationTabContainer,
    StyledSectionHeaderWrapper,
} from "app/pages/sales-intelligence/pages/manage-integration/styles/styles";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import type { ExportName } from "app/pages/sales-intelligence/sub-modules/management/store/type";
import { useIsCompanyAsLeadClaimEnabled } from "app/pages/sales-intelligence/pages/manage-export/hooks/useIsCompanyAsLeadClaimEnabled";
import { getCrmName } from "app/pages/sales-intelligence/utils/crm";

import {
    MANAGEMENT_EXPORT_PREFERENCES_EDIT_BUTTON,
    MANAGEMENT_EXPORT_PREFERENCES_SUBTITLE,
    MANAGEMENT_EXPORT_PREFERENCES_TITLE,
    MANAGEMENT_EXPORT_PREFERENCES_TITLE_TOOLTIP,
} from "../../../../common-components/export-table/translations";
import ExportToggle from "../ExportToggle/ExportToggle";
import { ExportToggleModal } from "../ExportToggleModal/ExportToggleModal";
import { MissingMandatoryFieldsModal } from "../MissingMandatoryFieldsModal/MissingMandatoryFieldsModal";
import { ExportIntegrations } from "app/pages/sales-intelligence/pages/management/types/types";
const EXPORT_TOGGLES: ExportName[] = ["accounts", "contacts"];

type Props = {
    integrationName: Integration;
    onEditMapping: VoidFunction;
    isEditDisabled: boolean;
};

const ExportPreferences = ({ integrationName, onEditMapping, isEditDisabled }: Props) => {
    const { isThanksModalOpen, openThanksModal, closeThanksModal } = useContactUsThanksModal();
    usePrefetchQuery(accountManagersQueryOptions);
    const isCompanyAsLeadExportClaimEnabled = useIsCompanyAsLeadClaimEnabled(integrationName);

    const renderEditButton = (isReadOnly: boolean) => (
        <Button
            onClick={onEditMapping}
            type="primary"
            isDisabled={isEditDisabled || isReadOnly}
            dataAutomation="manage-integration.edit-mapping-button"
        >
            {translate(MANAGEMENT_EXPORT_PREFERENCES_EDIT_BUTTON)}
        </Button>
    );

    const toggles = useMemo(() => {
        const newToggles = [...EXPORT_TOGGLES];

        if (integrationName === ExportIntegrations.salesforce || integrationName === ExportIntegrations.dynamics) {
            newToggles.push(isCompanyAsLeadExportClaimEnabled ? "companyAsLeads" : "leads");
        }

        return newToggles;
    }, [integrationName, isCompanyAsLeadExportClaimEnabled]);

    return (
        <StyledIntegrationTabContainer>
            <StyledSectionHeaderWrapper>
                <StyledEnrichmentHeaderWrapper>
                    <div>
                        <StyledSectionTitle>
                            {translate(MANAGEMENT_EXPORT_PREFERENCES_TITLE, {
                                crm: getCrmName(integrationName),
                            })}
                            {integrationName === "hubspot" ? (
                                <InfoIconTooltip tooltipKey={MANAGEMENT_EXPORT_PREFERENCES_TITLE_TOOLTIP.hubspot} />
                            ) : null}
                        </StyledSectionTitle>
                        <StyledSectionSubtitle>
                            {translate(MANAGEMENT_EXPORT_PREFERENCES_SUBTITLE, {
                                integrationName: getCrmName(integrationName),
                            })}
                        </StyledSectionSubtitle>
                    </div>
                    <CrmManagementDisabledTooltip renderContent={renderEditButton} />
                </StyledEnrichmentHeaderWrapper>
            </StyledSectionHeaderWrapper>
            {isCrmJobsTransitionProvider(integrationName) ? (
                <CrmIndustryTransitionAlertBanner
                    crm={integrationName}
                    variant="fieldMappings"
                />
            ) : null}
            {toggles.map((toggleName) => (
                <ExportToggle
                    key={`EXPORT-${toggleName}`}
                    toggleName={toggleName}
                    integrationName={integrationName}
                    onGainAccessClick={openThanksModal}
                />
            ))}
            <ExportToggleModal />
            <MissingMandatoryFieldsModal />
            <ContactUsThanksPortableModal
                isOpen={isThanksModalOpen}
                onClose={closeThanksModal}
            />
        </StyledIntegrationTabContainer>
    );
};

export default ExportPreferences;
